/**
 * The one place an UNAUTHENTICATED API request is performed.
 *
 * WHY THIS IS NOT `apiClient`
 *
 * Every route reachable through `apiClient` authenticates with the tenant's
 * session JWT. These routes do not — they authenticate with a capability token
 * that is already in the URL, handed to a customer in an emailed magic link.
 * The customer has no FieldIQ login and never will.
 *
 * Sending such a request through `apiClient` would do two specific, harmful
 * things:
 *
 *   1. The request interceptor attaches `Authorization: Bearer <jwt>` whenever
 *      a token happens to be in storage. On the customer-approval page that is
 *      the case exactly when a FieldIQ employee opens the customer's link on
 *      their own phone to check on it — so the tenant's session JWT would be
 *      transmitted on a route that has no business seeing it, to authenticate
 *      a decision that is supposed to be the customer's.
 *
 *   2. The response interceptor treats 401 as "this session is over": it wipes
 *      the stored token and sets `window.location.href = '/login'`. A 401 is a
 *      NORMAL outcome here — it is what an expired review link looks like — so
 *      a customer would be redirected to a login screen for an account they do
 *      not have, and an employee would be silently signed out of the app by
 *      opening a link.
 *
 * So the request stays a `fetch`. But exactly one of them, living in
 * `src/api/`, carrying the same guarantee `apiClient` gives every other
 * request since PR #289: it is BOUNDED. Before this, a stalled request on the
 * approval page never settled — `setSubmitting(false)` lives in a `finally`
 * that was never reached, so the customer watched "Approving..." forever with
 * no way to tell it from slow.
 *
 * The same chokepoint reasoning as `putToPresignedUrl`: the rule that a public
 * request must be bounded and must NOT carry a token is inherited here rather
 * than re-derived at each call site, where it has to be remembered — and
 * therefore eventually forgotten.
 *
 * WHAT THIS RETURNS, AND WHY IT IS A `Response`
 *
 * A resolved value means the server ANSWERED — not that it succeeded. Callers
 * still read `res.ok`, and that is deliberate: on this page the distinction
 * between 404 (link invalid or expired), 409 (ticket no longer reviewable) and
 * 500 (our problem) is three different screens for the customer, not one error
 * string. Collapsing them into a thrown error the way axios does would throw
 * that away. What the caller can no longer do is hang.
 */

import { API_URL } from './client';

/**
 * Budget for one public request.
 *
 * Deliberately the same 30s `apiClient` gives ordinary CRUD (`DEFAULT_TIMEOUT_MS`)
 * — a ticket review is an ordinary-sized JSON round trip, and having two
 * different answers to "how long may a request take" is how they drift apart.
 * The customer is looking at the screen the whole time, which is the window
 * that number is chosen for.
 */
export const PUBLIC_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Budget for a public request that carries a FILE.
 *
 * The same 300s `apiClient` gives an authenticated upload (`UPLOAD_TIMEOUT_MS`),
 * and for the same reason: this is a consultant photographing a signed ticket on
 * a wellsite, on one bar, and the cap is 20 MB. 30s is a perfectly good budget
 * for a JSON round trip and a guaranteed failure for that — the upload would
 * abort mid-transfer and report "the server took too long", which is both wrong
 * and unactionable when the answer is "it was still going".
 *
 * Applied at the CHOKEPOINT, keyed off the body being a `FormData`, rather than
 * remembered at each upload call site. Exactly the shape of `resolveTimeout` in
 * `client.ts`, which reaches the same conclusion from the same evidence.
 */
export const PUBLIC_UPLOAD_TIMEOUT_MS = 300_000;

const PUBLIC_FETCH_FAILED_FLAG = '__fieldiqPublicFetchFailed';

/**
 * Why the request did not produce an answer.
 *
 * - `timeout` — no response inside the budget. We gave up; the server may or
 *               may not have acted, which is why the copy says "try again"
 *               rather than "it failed".
 * - `network` — no response at all. Nothing was rejected; we never got there.
 */
export type PublicFetchFailureKind = 'timeout' | 'network';

export interface PublicFetchError extends Error {
  __fieldiqPublicFetchFailed: true;
  kind: PublicFetchFailureKind;
  /** A sentence safe to show a customer who has never heard of FieldIQ. */
  userMessage: string;
}

/**
 * Property check rather than `instanceof`: tsconfig targets es5, where
 * subclassing Error breaks the prototype chain (same reasoning as
 * `isUploadFailedError` in `api/upload` and `isTimeoutError` in
 * `lib/with-timeout`).
 */
export function isPublicFetchError(err: unknown): err is PublicFetchError {
  return !!err && (err as any)[PUBLIC_FETCH_FAILED_FLAG] === true;
}

function publicFetchFailed(
  kind: PublicFetchFailureKind,
  message: string,
  userMessage: string
): PublicFetchError {
  const err = new Error(message) as PublicFetchError;
  err.name = 'PublicFetchError';
  (err as any)[PUBLIC_FETCH_FAILED_FLAG] = true;
  err.kind = kind;
  err.userMessage = userMessage;
  return err;
}

/**
 * Message to show for a public request that never produced a response.
 *
 * The counterpart to `getApiErrorMessage` for this path. A timeout and a dead
 * network are different sentences: "took too long" tells the customer the
 * link is fine and the retry button is worth pressing; "check your connection"
 * tells them to look at their own phone first.
 */
export function getPublicFetchMessage(err: unknown, fallback: string): string {
  return isPublicFetchError(err) ? err.userMessage : fallback;
}

export interface PublicFetchOptions {
  /** Override the default budget. */
  timeoutMs?: number;
  /** Caller's own cancellation (component unmount, user cancel). */
  signal?: AbortSignal;
}

/** The budget a request gets when its caller named none. Exported to be testable. */
export function defaultTimeoutFor(body: unknown): number {
  if (typeof FormData !== 'undefined' && body instanceof FormData) return PUBLIC_UPLOAD_TIMEOUT_MS;
  return PUBLIC_REQUEST_TIMEOUT_MS;
}

/**
 * Request `path` against the tenant API without a session token, or throw.
 *
 * `path` is relative to the API base (e.g. `/ticket-approval/approve`), so the
 * base is resolved in ONE place — `API_URL` in `client.ts`, which is
 * `isNative`-aware. A page that builds its own base from
 * `process.env.REACT_APP_API_URL` gets a different answer on a Capacitor build,
 * where the env var is not baked in and the production URL is mandatory.
 *
 * No `Authorization` header is attached, and none may be: `init.headers` is
 * merged UNDER a deliberate deletion of it, so a caller cannot add one by
 * accident. If you need an authenticated request, you want `apiClient`.
 */
export async function publicApiFetch(
  path: string,
  init: RequestInit = {},
  options: PublicFetchOptions = {}
): Promise<Response> {
  const timeoutMs = options.timeoutMs ?? defaultTimeoutFor(init.body);
  const controller = new AbortController();

  // Distinguishes "we gave up" from "the caller cancelled" — the abort itself
  // looks identical from `fetch`'s side.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const forwardAbort = () => controller.abort();
  const external = options.signal;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', forwardAbort);
  }

  // Strip any Authorization a caller supplied. Header names are
  // case-insensitive on the wire but a plain object's keys are not, so every
  // spelling has to go.
  const headers: Record<string, string> = {};
  const supplied = (init.headers ?? {}) as Record<string, string>;
  for (const key of Object.keys(supplied)) {
    if (key.toLowerCase() === 'authorization') continue;
    headers[key] = supplied[key];
  }

  try {
    return await fetch(`${API_URL}${path}`, {
      ...init,
      headers,
      signal: controller.signal,
    });
  } catch (err: any) {
    if (timedOut) {
      throw publicFetchFailed(
        'timeout',
        `Public request to ${path} did not settle within ${timeoutMs}ms`,
        'The server took too long to respond. Please try again.'
      );
    }
    // The caller cancelled deliberately — not a failure, and not ours to relabel.
    if (external?.aborted) throw err;
    throw publicFetchFailed(
      'network',
      `Public request to ${path} failed: ${err?.message || 'network error'}`,
      'Unable to connect. Please check your internet connection and try again.'
    );
  } finally {
    clearTimeout(timer);
    if (external) external.removeEventListener('abort', forwardAbort);
  }
}

// ─── Customer approval stamps (api#709) ─────────────────────────────────────
//
// `data.stamp` on GET /ticket-approval/review/:token. Snake_case, because the
// route builds this object by hand (lib/approval-stamp.ts), unlike the camelCase
// ticket fields around it. Two shapes, told apart by `frozen`:
//
//   frozen: false — the LIVE template, sent only while the ticket is
//                   office_approved, with each row's bound ticket value prefilled.
//   frozen: true  — the copy SIGNED at approval. Labels and values only; the
//                   template's field ids are not stable, so the copy holds none.
//
// `null` means no stamp: today's approval page, unchanged.

/** One fillable row of a live stamp. */
export interface ReviewStampRow {
  field_id: string;
  label: string;
  field_type: string;
  /** Always false on a read-only row — the approver cannot supply it. */
  required: boolean;
  placeholder: string | null;
  help_text: string | null;
  choices: string[] | null;
  /** An allow-listed `ticket.*` binding, or null for an ordinary row. */
  binding: string | null;
  /** Computed by the server from the ticket; any value sent for it is ignored. */
  read_only: boolean;
  /** The bound ticket value, or '' when the row prefills nothing. */
  value: string;
}

export type StampSignoffRole = 'signer_name' | 'signature' | 'signed_at';

export interface ReviewStampLive {
  frozen: false;
  template_id: string;
  template_version: number;
  operator_name: string;
  operator_color: string | null;
  rows: ReviewStampRow[];
  signoff: { field_id: string; label: string; role: StampSignoffRole }[];
  requires_signer_name: boolean;
  requires_signature: boolean;
}

export interface ReviewStampFrozen {
  frozen: true;
  template_id: string;
  template_version: number;
  operator_name: string;
  operator_color: string | null;
  rows: { label: string; value: string }[];
  signoff: { label: string; role: StampSignoffRole }[];
  signer_name: string;
  /** Equal to customer_approved_at. */
  signed_at: string | null;
  /** signed_at's calendar day in the tenant's zone — what the PDF prints. */
  signed_on: string | null;
  /** The image itself is never returned to the page, only whether one is stored. */
  has_signature: boolean;
}

export type ReviewStamp = ReviewStampLive | ReviewStampFrozen;

// ─── Approval by uploaded file (api#721) ────────────────────────────────────
//
// The SECOND way a customer can approve, and the database enforces that it is a
// second way rather than an additional one: `tickets_customer_stamp_xor_file_chk`
// permits `customer_stamp` or `customer_stamp_file`, never both. The upload
// route pre-checks and answers 409 "This ticket already carries a recorded
// customer approval." rather than letting the constraint surface as a 23514, but
// the page still has to present the two paths as a CHOICE — a form that lets
// someone fill a stamp and attach a file has already lied about the data model.
//
// `data.stamped_file` on GET /ticket-approval/review/:token, snake_case, with a
// camelCase `stampedFile` alias alongside it. Two fields and no more: the route
// deliberately projects only the filename and the timestamp out of the jsonb
// column, so `r2_key`, `sha256`, `content_type` and `byte_size` are NOT on this
// unauthenticated payload and there is no way to reach the bytes from this page.
// Do not add one. The office downloads it through an authenticated route.

/** What the public review payload says about an uploaded approval. */
export interface StampedFileRef {
  /** Server-sanitised display name. Never a path, never an R2 key. */
  filename: string;
  /** Full ISO-8601 instant, equal to `customer_approved_at`. Null if absent. */
  uploaded_at: string | null;
}

/**
 * Field names for POST /ticket-approval/:token/upload, which is multipart.
 *
 * Named constants because they are the one part of this feature TypeScript
 * cannot check: a `FormData` key is a string on the wire, so `approver_name`
 * compiles perfectly and earns a 400 "Approver name is required" from a server
 * reading `approverName`. Verified against `ticket-approval.routes.ts:800,808`.
 */
export const STAMPED_UPLOAD_FILE_FIELD = 'file';
export const STAMPED_UPLOAD_NAME_FIELD = 'approverName';

/** Body of POST /ticket-approval/approve. The stamp keys are sent only when the page has a stamp. */
export interface ApproveTicketBody {
  token: string;
  approverName: string;
  approverTitle?: string;
  /** Keyed by `field_id`. A key sent as '' means the approver cleared the row. */
  stampValues?: Record<string, string>;
  /** `canvas.toDataURL('image/png')`. */
  signature?: string;
  /** Lets the server refuse with 409 when the office edited the stamp after this page loaded. */
  stampTemplateVersion?: number;
}
